/** Country-level lower bounds from the Shopify shipping profile. Amounts are cents, weights grams. */
export function estimateShipping(cart, country, table, currency = null) {
  if (!cart || !Array.isArray(cart.items) || !cart.items.length) return { state: 'empty' };
  const total = Number(cart.total_price);
  if (!Number.isFinite(total) || total < 0) return { state: 'unavailable' };
  const giftValue = cart.items.reduce((sum, item) => (
    String(item.properties?._bb_gift_roulette) === 'true' ? sum + Number(item.final_line_price || 0) : sum
  ), 0);
  const subtotal = Math.max(0, total - giftValue);
  const shippingItems = cart.items.filter(item => item.requires_shipping !== false);
  if (!shippingItems.length) return { state: 'ready', subtotal, shipping: 0, total: subtotal, weight: 0 };
  if (!table) return { state: 'unavailable', subtotal };
  let conversionRate = 1;
  let roundingStep = 1;
  if (cart.currency !== table.currency) {
    const conversion = table.conversions?.[cart.currency];
    if (conversion?.country !== country || currency?.active !== cart.currency || !(Number(currency.rate) > 0) || !Number.isFinite(Number(currency.rate))) return { state: 'unavailable', subtotal };
    conversionRate = Number(currency.rate);
    roundingStep = conversion.roundingStep;
    if (!(roundingStep > 0) || (table.countries?.[country] || []).some(rate => rate.minSubtotal !== undefined || rate.maxSubtotal !== undefined)) return { state: 'unavailable', subtotal };
  }
  if (shippingItems.some(item => table.excludedVariants?.includes(Number(item.variant_id)))) return { state: 'unavailable', subtotal };
  const weight = Number.isFinite(cart.total_weight) ? cart.total_weight : shippingItems.reduce((sum, item) => sum + Number(item.grams) * Number(item.quantity), 0);
  if (!Number.isFinite(weight) || weight < 0) return { state: 'unavailable', subtotal };
  const eligible = (table.countries?.[country] || []).filter(rate => (
    Number.isFinite(rate.price) && rate.price >= 0 &&
    weight >= (rate.minWeight ?? 0) && weight <= (rate.maxWeight ?? Infinity) &&
    subtotal >= (rate.minSubtotal ?? 0) && subtotal <= (rate.maxSubtotal ?? Infinity)
  ));
  if (!eligible.length) return { state: 'unavailable', subtotal, weight };
  const cheapest = Math.min(...eligible.map(rate => rate.price));
  const shipping = cart.currency === table.currency ? cheapest : Math.ceil(cheapest * conversionRate / roundingStep - 1e-9) * roundingStep;
  return { state: 'ready', subtotal, shipping, total: subtotal + shipping, weight };
}

if (typeof document !== 'undefined' && !customElements.get('bb-cart-shipping')) {
  let latestCart = null;
  let country = '';
  let revision = 0;
  let cartRequest = null;
  const tables = new Map();
  const components = new Set();
  const validCart = cart => Array.isArray(cart?.items) && Number.isFinite(cart.total_price);
  const renderAll = () => components.forEach(component => component.render());
  const updateCart = cart => {
    latestCart = cart;
    revision++;
    renderAll();
    // Cart events fire before the theme replaces the section HTML.
    queueMicrotask(renderAll);
  };
  const refreshCart = async () => {
    cartRequest?.abort();
    const controller = new AbortController();
    cartRequest = controller;
    const timeout = setTimeout(() => controller.abort(), 10000);
    const requestedRevision = revision;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal: controller.signal });
      if (!response.ok) throw Error('Cart unavailable');
      const cart = await response.json();
      if (requestedRevision === revision && validCart(cart)) updateCart(cart);
    } catch {
      if (requestedRevision === revision) components.forEach(component => component.unavailable());
    } finally {
      clearTimeout(timeout);
      if (cartRequest === controller) cartRequest = null;
    }
  };
  const onCartChange = event => {
    const cart = event.detail?.cart || event.detail?.resource;
    if (validCart(cart)) updateCart(cart);
    else {
      revision++;
      components.forEach(component => component.unavailable());
      refreshCart();
    }
  };
  document.addEventListener('blankabric:cart-updated', onCartChange);
  document.addEventListener('cart:update', onCartChange);
  document.addEventListener('discount:update', onCartChange);

  class CartShipping extends HTMLElement {
    static observedAttributes = ['data-cart'];
    connectedCallback() {
      components.add(this);
      if (!country) country = this.dataset.country;
      if (!latestCart) this.readCart();
      this.addEventListener('change', this.changeCountry);
      const url = this.dataset.ratesUrl;
      if (!tables.has(url)) tables.set(url, fetch(url, { signal: AbortSignal.timeout(10000) }).then(response => {
        if (!response.ok) throw Error('Rates unavailable');
        return response.json();
      }).catch(() => null));
      tables.get(url).then(table => { this.table = table; this.render(); });
    }
    disconnectedCallback() {
      components.delete(this);
      this.removeEventListener('change', this.changeCountry);
    }
    attributeChangedCallback() {
      if (this.isConnected) { this.readCart(); queueMicrotask(renderAll); }
    }
    readCart() {
      try {
        const cart = JSON.parse(this.dataset.cart);
        if (validCart(cart)) { latestCart = cart; revision++; }
      } catch { /* Keep the last complete cart. */ }
    }
    changeCountry = event => {
      if (!event.target.matches('[data-bb-shipping-country]')) return;
      country = event.target.value;
      renderAll();
      // Use Shopify's country preference so currency and free-shipping messages stay consistent.
      event.target.form?.requestSubmit();
    };
    unavailable() {
      const price = this.querySelector('[data-bb-shipping-price]');
      const row = this.querySelector('[data-bb-shipping-total-row]');
      if (price) price.textContent = this.dataset.unavailable;
      if (row) row.hidden = true;
    }
    render() {
      if (!this.isConnected) return;
      const result = estimateShipping(latestCart, country, this.table, window.Shopify?.currency);
      this.hidden = result.state === 'empty';
      if (this.hidden) return;
      const select = this.querySelector('[data-bb-shipping-country]');
      if (select) select.value = country;
      let money;
      try {
        money = new Intl.NumberFormat(document.documentElement.lang || 'fr', {style:'currency',currency:latestCart.currency});
      } catch { this.unavailable(); return; }
      const format = cents => money.format(cents / 100);
      const container = this.closest('.bb-cart-drawer__footer, .cart-totals__container');
      const subtotal = container?.querySelector('[data-bb-cart-total], [data-cart-subtotal]');
      if (subtotal && Number.isFinite(result.subtotal)) subtotal.textContent = format(result.subtotal);
      if (result.state !== 'ready') { this.unavailable(); return; }
      const from = cents => this.dataset.from.replace('[amount]', format(cents));
      this.dataset.weight = String(result.weight);
      this.querySelector('[data-bb-shipping-price]').textContent = from(result.shipping);
      this.querySelector('[data-bb-shipping-total]').textContent = from(result.total);
      this.querySelector('[data-bb-shipping-total-row]').hidden = false;
    }
  }
  customElements.define('bb-cart-shipping', CartShipping);
  window.addEventListener('pageshow', event => { if (event.persisted) refreshCart(); });
}
